import type {
  CreateUserInput,
  ProjectUser,
  RoleChangeInput,
  StubMergeInput,
  StubReplaceInput,
  UnassignedUser,
} from '@testflow/shared';
import { request } from './api.js';

const base = (p: string) => `/v1/projects/${p}`;

export const usersApi = {
  list(projectId: string): Promise<ProjectUser[]> {
    return request<ProjectUser[]>(`${base(projectId)}/users`);
  },
  listUnassigned(projectId: string): Promise<UnassignedUser[]> {
    return request<UnassignedUser[]>(`${base(projectId)}/users/unassigned`);
  },
  create(projectId: string, input: CreateUserInput): Promise<{ user_id: string }> {
    return request<{ user_id: string }>(`${base(projectId)}/users`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  changeRoles(
    projectId: string,
    userId: string,
    change: RoleChangeInput,
  ): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/users/${userId}/roles`, {
      method: 'PATCH',
      body: JSON.stringify(change),
    });
  },
  setActive(projectId: string, userId: string, is_active: boolean): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active }),
    });
  },
  // Task 13: E-Mail/Name pflegen (u.a. Stub-User aus dem Import auf eine echte Mail umbiegen —
  // der Server räumt dabei is_stub ab). Teilt sich die Route mit setActive (siehe users.ts).
  updateUser(
    projectId: string,
    userId: string,
    patch: { email?: string; name?: string },
  ): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  // #130: Platzhalter direkt zum echten Nutzer machen (E-Mail, Name, Typ, Rolle in EINEM Schritt) —
  // ersetzt den Inline-Weg über updateUser, der den Nutzer inaktiv und ohne Rolle zurückließ.
  replaceStub(
    projectId: string,
    userId: string,
    input: StubReplaceInput,
  ): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/users/${userId}/replace-stub`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  // #130: Platzhalter in einen bestehenden Nutzer zusammenführen (alle Verweise wandern, der
  // Platzhalter wird gelöscht). `moved` zählt je Tabelle die umgehängten Zeilen.
  mergeInto(
    projectId: string,
    userId: string,
    input: StubMergeInput,
  ): Promise<{ ok: boolean; moved: Record<string, number> }> {
    return request<{ ok: boolean; moved: Record<string, number> }>(
      `${base(projectId)}/users/${userId}/merge-into`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  },
};
